/**
 * The motion registry: every figure pose, motion and straight-limb arm, gathered from the motion
 * groups (one file each in render/motions/). Adding a pose or a motion means adding it to a group
 * (or a new group file listed here); `Pose`, `MotionId` and `NearArm` follow from the groups.
 *
 * Only this file imports the group files, and the groups use what they import from here only inside
 * their functions: a group imported first would find this file's lists not made yet.
 */
import { danceRig, isDance, type DancePose } from '../dances';
import { eased, FRAMES, type Ease, type LimbSpec, type NearArm, type PoseRig, type Rig } from './kit';
import * as core from './core';
import * as duelMoves from './duelMoves';
import * as emotes from './emotes';
import * as folk from './folk';
import * as guardWalk from './guardWalk';
import * as guardRead from './guardRead';
import * as hold from './hold';
import * as idles from './idles';
import { OVERRIDES, wushuFor } from './wushu';
import * as life from './life';
import * as long from './long';
import * as wx from './wx';
import * as qinggong from './qinggong';
import * as recovery from './recovery';
import * as skills from './skills';
import * as snowSword from './snowSword';
import * as stances from './stances';
import * as strikes from './strikes';
import * as receive from '../rig/receive';

export { stowPhase } from './core';
export { STANCE_COUNT, STANCE_MOTION_IDS, STANCE_PH, stanceOf } from './stances';

/** Each group's key poses, by name. A new group's `POSES` is added here. */
const POSE_GROUPS = [
  core.POSES,
  stances.POSES,
  qinggong.POSES,
  folk.POSES,
  life.POSES,
  emotes.POSES,
  strikes.POSES,
  skills.POSES,
  snowSword.POSES,
  receive.POSES,
  guardWalk.POSES,
  wx.POSES,
  idles.POSES,
  duelMoves.POSES,
  guardRead.POSES,
  long.POSES,
] as const;

/** Each group's straight-limb arms. A new group's `ARMS` is added here. */
const ARM_GROUPS = [strikes.ARMS, core.ARMS, snowSword.ARMS, hold.ARMS, wx.ARMS, guardRead.ARMS, long.ARMS] as const;

/** The names of every member of a union of records. */
type KeysOf<T> = T extends unknown ? keyof T : never;

/**
 * Every pose a figure is drawn in: each group's key poses (`POSE_GROUPS`) and the dances' key
 * positions (render/dances.ts). 'motion' and 'gaze' (core.ts) stand for a frame of a motion and a
 * look toward someone.
 */
export type Pose = KeysOf<(typeof POSE_GROUPS)[number]> | DancePose;

/** Arms drawn as a straight limb, from each group's `ARMS`. */
export type LimbArm = KeysOf<(typeof ARM_GROUPS)[number]>;

/**
 * Motions made of key poses — strikes, casting, the guard, a hit, landing, a sword form — drawn in
 * `FRAMES` frames, each part of the way from one key to the next (whole skeletons blended, see
 * `skeleton`), so a blow sweeps through instead of jumping between four stills. A looping motion
 * (a form practised over and over) runs round from its last key back to its first.
 *
 * `ease` gives each step between keys its own feel (one per step; a loop has one more, from its
 * last key back to its first; `inOut` where none is given). A blow gathers speed into the strike and
 * slows through its follow-through, so it reads as weight rather than a slide at one pace. How long
 * each step takes is the renderer's (`along` in renderer.ts).
 */
// Groups in a fixed order: a motion's place in this list is its phase (`motionPhase`), which baked
// frames are stored by. A new group's motions go at the end.
export const MOTIONS = [
  ...strikes.MOTIONS,
  ...qinggong.MOTIONS,
  ...recovery.MOTIONS,
  ...skills.MOTIONS,
  ...snowSword.MOTIONS,
  ...stances.MOTIONS,
  ...receive.MOTIONS,
  ...guardWalk.MOTIONS,
  ...wx.MOTIONS,
  ...idles.MOTIONS,
  ...duelMoves.MOTIONS,
  ...guardRead.MOTIONS,
  ...long.MOTIONS,
] as const;
export type MotionId = (typeof MOTIONS)[number]['id'];

/** The 'motion' pose's phase for frame `f` (0 to `FRAMES` − 1) of a motion. */
export function motionPhase(id: MotionId, f: number): number {
  const i = MOTIONS.findIndex((m) => m.id === id);
  return i * FRAMES + Math.max(0, Math.min(FRAMES - 1, Math.round(f)));
}

/**
 * The two key poses a motion frame lies between and how far along (eased, see `ease`): a run (not
 * looping) puts its first key on the first frame and its last on the last; a loop spreads its keys
 * round the cycle.
 */
export function motionKeys(ph: number): { a: Pose; b: Pose; t: number } {
  const m = MOTIONS[Math.floor(ph / FRAMES) % MOTIONS.length]!;
  const f = ph % FRAMES;
  const n = m.keys.length;
  const loop = 'loop' in m && m.loop;
  const u = loop ? (f / FRAMES) * n : (f / (FRAMES - 1)) * (n - 1);
  const i = Math.min(loop ? n - 1 : n - 2, Math.floor(u));
  const ease: readonly Ease[] = 'ease' in m ? m.ease : [];
  const t = eased(ease[i] ?? 'inOut', Math.max(0, Math.min(1, u - i)));
  return { a: m.keys[i]!, b: m.keys[(i + 1) % n]!, t };
}

/** Every group's poses by name (a name given twice is a mistake, found when the game starts). */
const RIGS: Record<string, PoseRig<never>> = {};
for (const g of POSE_GROUPS)
  for (const [k, f] of Object.entries(g)) {
    if (k in RIGS) throw new Error(`pose ${k} is defined twice in render/motions/`);
    RIGS[k] = f;
  }

export function rigFor(pose: Pose, ph: number): Rig {
  const r: Rig = {
    o: 0,
    lean: 0,
    tilt: 0,
    headDx: 0,
    f1: 0,
    f2: 0,
    l1: 0,
    l2: 0,
    sway: 0,
    armSwing: 0,
    hem: 0,
    flare: 0,
    arm: 'hang',
    far: 'hang',
    face: 'idle',
    closed: false,
  };
  // ท่าเต้น: the dances' key positions (render/dances.ts).
  if (isDance(pose)) return Object.assign(r, danceRig(pose, ph));
  Object.assign(r, (RIGS[pose] as PoseRig<Pose>)(ph, pose));
  // The wushu polish laid over the group's pose (wushu.ts, lane C0-POSE-FIX).
  const over = wushuFor(pose) ? OVERRIDES[pose] : undefined;
  return over ? Object.assign(r, over(ph)) : r;
}

/** Near-arm poses drawn as a straight limb: hand position (relative to cx, before `o`) and blade angle. */
export const LIMB: Partial<Record<NearArm, LimbSpec>> = Object.assign({}, ...ARM_GROUPS);
